// **Ein Lauf mit Fortschritt** — die Schritte mit Zustand, Fehlertext und
// Protokoll, „Erneut versuchen ab hier" und „Abbrechen". Fragt alle zwei
// Sekunden nach, solange der Lauf läuft (use-run-polling.ts), und hört auf,
// sobald die Karte nicht mehr gezeigt wird.

import { useState } from "react";
import { get, post } from "../../api/client";
import type { Run, RunStep } from "../../api/types";
import { fmtDate } from "../../lib/format";
import { RUN_KIND, STEP, errorText, runStateOf } from "../../lib/labels";
import { Badge, Note } from "../../ui/badge";
import { BusyButton } from "../../ui/busy-button";
import { Card } from "../../ui/card";
import { ConfirmButton } from "../../ui/confirm-button";
import { toast } from "../../ui/toaster";
import { openStepIndex, stepDuration } from "./run-helpers";
import { useRunPolling } from "./use-run-polling";

function Step({ s, open }: { s: RunStep; open: boolean }) {
  const log = s.log ?? [];
  return (
    <div className="pf-run__step">
      <span className={`pf-run__icon pf-run__icon--${s.state}`} aria-label={s.state} />
      <div>
        <div className="pf-run__label">{STEP[s.key] ?? s.key}</div>
        {s.error ? <div className="pf-run__error">{s.error}</div> : null}
        {log.length ? (
          <details className="pf-details" open={open}>
            <summary>Protokoll ({log.length})</summary>
            <div className="pf-log">{log.join("\n")}</div>
          </details>
        ) : null}
      </div>
      <span className="pf-run__time">{stepDuration(s)}</span>
    </div>
  );
}

type InviteOut = { url: string } | { error: string } | null;

export function RunCard({ tenantId, run, onFinished }: { tenantId: number; run: Run; onFinished?: (run: Run) => void }) {
  const { current, refresh, replace } = useRunPolling(tenantId, run, onFinished);
  const [invite, setInvite] = useState<InviteOut>(null);
  const [inviteTaken, setInviteTaken] = useState(false);
  const [stateText, tone] = runStateOf(current.state);
  const failed = current.state === "failed";
  const openStep = openStepIndex(current.steps);
  const showInvite = current.invite_available && !inviteTaken;

  const retry = async () => {
    try {
      await post(`/tenants/${tenantId}/runs/${current.id}/retry`);
      await refresh();
    } catch (e) { toast(errorText(e), "error"); }
  };
  const cancel = async () => {
    try {
      const res = await post<{ run: Run }>(`/tenants/${tenantId}/runs/${current.id}/cancel`);
      replace(res.run);
    } catch (e) { toast(errorText(e), "error"); }
  };
  const takeInvite = async () => {
    try {
      const res = await get<{ invite_url: string }>(`/tenants/${tenantId}/runs/${current.id}/invite`);
      setInvite({ url: res.invite_url });
      setInviteTaken(true);
    } catch (e) { setInvite({ error: errorText(e) }); }
  };

  const steps = !current.steps.length ? null : current.state === "done"
    // Ein fertiger Lauf ist Geschichte: seine Schritte klappen zu, statt die
    // Übersicht mit einer langen Liste grüner Punkte zu füllen.
    ? (
      <details key="steps" className="pf-details">
        <summary>{current.steps.length} Schritte</summary>
        <div className="pf-run">{current.steps.map((s, i) => <Step key={`${s.key}-${i}`} s={s} open={false} />)}</div>
      </details>
    )
    : <div key="steps" className="pf-run">{current.steps.map((s, i) => <Step key={`${s.key}-${i}`} s={s} open={i === openStep} />)}</div>;

  return (
    <Card
      title={<>{RUN_KIND[current.kind] ?? current.kind} <Badge tone={tone}>{stateText}</Badge></>}
      hint={<>gestartet {fmtDate(current.created_at)} von {current.started_by}{current.params.build ? <> · Build <code>{current.params.build}</code></> : current.params.image_tag ? <> · Image-Tag <code>{current.params.image_tag}</code></> : null}</>}
      actions={[
        failed && <BusyButton key="retry" className="pf-btn pf-btn--sm pf-btn--primary" onClick={retry}>Erneut versuchen ab hier</BusyButton>,
        failed && current.kind === "setup" && (
          <ConfirmButton key="cancel" className="pf-btn pf-btn--sm pf-btn--danger" confirmLabel="Wirklich zurückbauen?" onConfirm={cancel}>Abbrechen und zurückbauen</ConfirmButton>
        ),
        showInvite && <button key="invite" type="button" className="pf-btn pf-btn--sm" onClick={() => void takeInvite()}>Einladungslink anzeigen</button>,
      ]}
    >
      {[
        steps,
        failed && current.kind === "rollout" && (
          <Note key="rollout" tone="warn">Ein gescheiterter Rollout lässt die Wartung <strong>an</strong> — die Instanz weist Prompts ab, bis jemand sie abschaltet (Betrieb → Wartung aus).</Note>
        ),
        failed && current.kind === "setup" && (
          <Note key="setup">„Erneut versuchen“ setzt beim gescheiterten Schritt wieder an. „Abbrechen“ baut alles, was schon angelegt ist, in umgekehrter Reihenfolge zurück; der Entwurf bleibt.</Note>
        ),
        invite && (
          <div key="invite">
            {"url" in invite
              ? <Note tone="warn">Einladungslink — wird nur <strong>dieses eine Mal</strong> angezeigt:<div className="pf-copy"><input className="pf-input" readOnly value={invite.url} /></div></Note>
              : <Note tone="error">{invite.error}</Note>}
          </div>
        ),
      ]}
    </Card>
  );
}
